import React, { useState, useEffect, useRef } from 'react';
import { acceptCall, declineCall, cancelCall } from '../../../api/v2/calls';
import {
  formatScheduledAt,
  expandSlotsToThirtyMinuteChoices,
  formatSlotRangeInTimezone,
  formatTimezoneAbbr,
} from '../../../helpers/timezone_utils';
import { maskPhoneNumber } from '../../../helpers';
import { failToast } from '../../ui_kit/utilities/fail_toast';

import styles from './index.module.css';

const getSecondsRemaining = (expiresAt) => {
  if (!expiresAt) return 0;
  return Math.max(0, Math.floor((new Date(expiresAt) - new Date()) / 1000));
};

const formatCountdown = (seconds) => {
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  }
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

function formatSlotChoice(slot, timezone, tzAbbr) {
  const endISO = slot.end_at || new Date(new Date(slot.start_at).getTime() + 30 * 60 * 1000).toISOString();
  return formatSlotRangeInTimezone(slot.start_at, endISO, timezone, tzAbbr);
}

export default function MentorCallStatus({
  incomingCall,
  acceptedCall,
  patientFirstName,
  mentorTimezone,
  mentorPhone,
  onAccepted,
  onDeclined,
  onCounter,
  onCancel,
}) {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const [confirmingCounter, setConfirmingCounter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [countdown, setCountdown] = useState(getSecondsRemaining(incomingCall?.acceptance_expires_at));

  useEffect(() => {
    const expiresAt = incomingCall?.acceptance_expires_at;
    if (!expiresAt) return;

    setCountdown(getSecondsRemaining(expiresAt));
    const interval = setInterval(() => {
      const remaining = getSecondsRemaining(expiresAt);
      setCountdown(remaining);
      if (remaining === 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [incomingCall?.acceptance_expires_at]);

  const isScheduled = incomingCall?.scheduled;
  const proposedSlots = incomingCall?.proposed_slots || [];
  const slotChoices = isScheduled ? expandSlotsToThirtyMinuteChoices(proposedSlots) : [];
  const mentorTz = mentorTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const mentorTzAbbr = formatTimezoneAbbr(mentorTz);

  if (acceptedCall && (acceptedCall.status === 'accepted' || acceptedCall.status === 'in_progress')) {
    return (
      <MentorAcceptedCallStatus
        call={acceptedCall}
        patientFirstName={patientFirstName}
        mentorTimezone={mentorTimezone}
        mentorPhone={mentorPhone}
        onCancel={onCancel}
      />
    );
  }

  if (!incomingCall) return null;

  if (incomingCall.countered_by_type === 'PatientPartner') {
    return (
      <MentorWaitingForPatient
        incomingCall={incomingCall}
        patientFirstName={patientFirstName}
        countdown={countdown}
        onCanceled={onDeclined}
      />
    );
  }

  const handleAccept = () => {
    setLoading(true);
    const scheduledAt = isScheduled && selectedSlot ? selectedSlot.start_at : undefined;
    acceptCall({ callId: incomingCall.uuid, scheduledAt })
      .then((updatedCall) => {
        onAccepted(updatedCall);
      })
      .catch((err) => {
        failToast(err.message || 'Could not accept. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  const handleDecline = () => {
    setLoading(true);
    declineCall({ callId: incomingCall.uuid })
      .then(() => {
        onDeclined();
      })
      .catch((err) => {
        failToast(err.message || 'Could not decline. Please try again.');
      })
      .finally(() => {
        setLoading(false);
        setConfirmingDecline(false);
      });
  };

  const handleCounterConfirm = () => {
    setConfirmingCounter(false);
    onCounter();
  };

  if (confirmingDecline) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.label}>Decline this call request?</span>
        </div>
        <div className={styles.confirm_row}>
          <button className={styles.decline_btn} onClick={handleDecline} disabled={loading}>
            {loading ? 'Declining...' : 'Yes, decline'}
          </button>
          <button className={styles.counter_btn} onClick={() => setConfirmingDecline(false)} disabled={loading}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  if (confirmingCounter) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.label}>
            Propose new times to {patientFirstName}? They'll be notified to pick from your availability.
          </span>
        </div>
        <div className={styles.confirm_row}>
          <button className={styles.accept_btn} onClick={handleCounterConfirm}>
            Continue
          </button>
          <button className={styles.counter_btn} onClick={() => setConfirmingCounter(false)}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (isScheduled) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.header_row} onClick={() => setCollapsed((c) => !c)}>
          <div className={styles.title_row}>
            <PhoneIcon />
            <span className={styles.label}>{patientFirstName} wants to schedule a call — pick a time to accept</span>
          </div>
          <CollapseChevron collapsed={collapsed} />
        </div>
        {!collapsed && (
          <>
            <div className={styles.slots}>
              {slotChoices.map((choice) => {
                const isSelected = selectedSlot?.start_at === choice.start_at;
                return (
                  <button
                    key={choice.start_at}
                    className={`${styles.slot_btn} ${isSelected ? styles.slot_btn_selected : ''}`}
                    onClick={() => setSelectedSlot(isSelected ? null : choice)}
                  >
                    {formatSlotChoice(choice, mentorTz, mentorTzAbbr)}
                  </button>
                );
              })}
            </div>
            <div className={styles.actions}>
              <button className={styles.accept_btn} onClick={handleAccept} disabled={loading || !selectedSlot}>
                {loading ? 'Accepting...' : 'Accept selected time'}
              </button>
              <button className={styles.decline_btn} onClick={() => setConfirmingDecline(true)} disabled={loading}>
                Decline
              </button>
              <button className={styles.counter_btn} onClick={() => setConfirmingCounter(true)} disabled={loading}>
                Counter with new times
              </button>
            </div>
            {countdown > 0 && <span className={styles.expires}>Expires in {formatCountdown(countdown)}</span>}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header_row} onClick={() => setCollapsed((c) => !c)}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.label}>{patientFirstName} wants to connect right now</span>
        </div>
        <CollapseChevron collapsed={collapsed} />
      </div>
      {!collapsed && (
        <>
          <span className={styles.hint}>Make sure your phone is available</span>
          <div className={styles.actions}>
            <button className={styles.accept_btn} onClick={handleAccept} disabled={loading}>
              {loading ? 'Accepting...' : 'Accept'}
            </button>
            <button className={styles.decline_btn} onClick={() => setConfirmingDecline(true)} disabled={loading}>
              Decline
            </button>
          </div>
          {countdown > 0 && <span className={styles.expires}>Expires in {formatCountdown(countdown)}</span>}
        </>
      )}
    </div>
  );
}

function MentorWaitingForPatient({ incomingCall, patientFirstName, countdown, onCanceled }) {
  const [confirming, setConfirming] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleCancel = () => {
    setCanceling(true);
    cancelCall({ callId: incomingCall.uuid })
      .then(() => {
        onCanceled();
      })
      .catch((err) => {
        failToast(err.message || 'Could not cancel. Please try again.');
      })
      .finally(() => {
        if (isMounted.current) setCanceling(false);
      });
  };

  if (confirming) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.label}>Cancel this call?</span>
        </div>
        <div className={styles.confirm_row}>
          <button className={styles.decline_btn} onClick={handleCancel} disabled={canceling}>
            {canceling ? 'Canceling...' : 'Yes, cancel'}
          </button>
          <button className={styles.keep_btn} onClick={() => setConfirming(false)} disabled={canceling}>
            Keep it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header_row} onClick={() => setCollapsed((c) => !c)}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.label}>Waiting for {patientFirstName} to pick a time</span>
        </div>
        <CollapseChevron collapsed={collapsed} />
      </div>
      {!collapsed && (
        <>
          <span className={styles.hint}>They've been notified of your proposed times</span>
          <div className={styles.actions}>
            <button className={styles.decline_btn} onClick={() => setConfirming(true)}>
              Cancel
            </button>
          </div>
          {countdown > 0 && <span className={styles.expires}>Expires in {formatCountdown(countdown)}</span>}
        </>
      )}
    </div>
  );
}

function MentorAcceptedCallStatus({ call, patientFirstName, mentorTimezone, mentorPhone, onCancel }) {
  const [confirming, setConfirming] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleCancel = () => {
    setCanceling(true);
    cancelCall({ callId: call.uuid })
      .then(() => {
        onCancel();
      })
      .catch((err) => {
        failToast(err.message || 'Could not cancel. Please try again.');
      })
      .finally(() => {
        if (isMounted.current) setCanceling(false);
      });
  };

  const { status, scheduled_at, scheduled } = call;

  if (confirming) {
    return (
      <div className={`${styles.wrapper} ${styles.wrapper_accepted}`}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.label}>Cancel this call?</span>
        </div>
        <div className={styles.confirm_row}>
          <button className={styles.decline_btn} onClick={handleCancel} disabled={canceling}>
            {canceling ? 'Canceling...' : 'Yes, cancel'}
          </button>
          <button className={styles.keep_btn} onClick={() => setConfirming(false)} disabled={canceling}>
            Keep it
          </button>
        </div>
      </div>
    );
  }

  const maskedPhone = maskPhoneNumber(mentorPhone);
  const acceptedSummary =
    status === 'in_progress'
      ? 'Call in progress'
      : scheduled_at
        ? `Scheduled for ${formatScheduledAt(scheduled_at, mentorTimezone)}`
        : maskedPhone
          ? `You accepted, we'll call you shortly at ${maskedPhone}`
          : "You accepted, we'll call you shortly";

  return (
    <div className={`${styles.wrapper} ${styles.wrapper_accepted}`}>
      <div className={styles.header_row} onClick={() => setCollapsed((c) => !c)}>
        <div className={styles.title_row}>
          <PhoneIcon accepted />
          <span className={styles.label}>{acceptedSummary}</span>
        </div>
        <CollapseChevron collapsed={collapsed} accepted />
      </div>
      {!collapsed && (
        <>
          {!scheduled_at && !scheduled && status !== 'in_progress' && (
            <span className={styles.hint}>Keep your phone on. You can leave the app and we&apos;ll ring you.</span>
          )}
          {status !== 'in_progress' && (
            <div className={styles.actions}>
              <button className={styles.decline_btn} onClick={() => setConfirming(true)} disabled={canceling}>
                Cancel
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CollapseChevron({ collapsed, accepted }) {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      style={{
        flexShrink: 0,
        transition: 'transform 0.2s',
        transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
    >
      <path
        d='M18 15L12 9L6 15'
        stroke={accepted ? '#065f46' : '#a07a30'}
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function PhoneIcon({ accepted }) {
  return (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M20.9995 16.4767V19.1864C21.0037 20.2223 20.0723 21.0873 19.0265 20.9929C10.0001 21 3.00006 13.935 3.00713 4.96919C2.91294 3.92895 3.77364 3.00106 4.80817 3.00009H7.52331C7.96253 2.99577 8.38835 3.151 8.72138 3.43684C9.66819 4.24949 10.2772 7.00777 10.0429 8.10428C9.85994 8.96036 8.99696 9.55929 8.41026 10.1448C9.69864 12.4062 11.5747 14.2785 13.8405 15.5644C14.4272 14.9788 15.0274 14.1176 15.8851 13.935C16.9855 13.7008 19.7615 14.3106 20.5709 15.264C20.858 15.6021 21.0105 16.0337 20.9995 16.4767Z'
        stroke={accepted ? '#065f46' : '#7a5c00'}
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
